import api from 'services/api';

const AI_SESSIONS_FETCH_ALL = 'aiSessions/fetchAll';
const AI_SESSIONS_FETCH = 'aiSessions/fetch';
const AI_SESSIONS_CREATE = 'aiSessions/create';
const AI_SESSIONS_DELETE = 'aiSessions/delete';
const AI_SESSIONS_SEND_MESSAGE = 'aiSessions/sendMessage';
const AI_SESSIONS_UPDATE_CURRENT = 'aiSessions/updateCurrent';
const AI_SESSIONS_CLEAR_CURRENT = 'aiSessions/clearCurrent';
const AI_SESSIONS_APPEND_MESSAGE = 'aiSessions/appendMessage';
const AI_SESSIONS_START_PREVIEW = 'aiSessions/startPreview';
const AI_SESSIONS_STOP_PREVIEW = 'aiSessions/stopPreview';
const AI_SESSIONS_SET_TYPING = 'aiSessions/setTyping';

const MESSAGES_INCLUDE = {
  include: {
    messages: {
      url: '/aisessions:messages',
      params: { parent_id: 'id' },
      data: { limit: 0 },
    },
  },
};

const actions = {
  fetchAll() {
    return {
      type: AI_SESSIONS_FETCH_ALL,
      payload: api.get('/ai-sessions', MESSAGES_INCLUDE),
    };
  },

  fetch(id) {
    return {
      type: AI_SESSIONS_FETCH,
      payload: api.get(`/ai-sessions/${id}`, MESSAGES_INCLUDE),
    };
  },

  // Fetch session without messages (metadata only), merges into current
  fetchMeta(id) {
    return {
      type: AI_SESSIONS_UPDATE_CURRENT,
      payload: api.get(`/ai-sessions/${id}`),
    };
  },

  create(data) {
    return {
      type: AI_SESSIONS_CREATE,
      payload: api.post('/ai-sessions', data),
    };
  },

  delete(id) {
    return {
      type: AI_SESSIONS_DELETE,
      payload: api.delete(`/ai-sessions/${id}`),
    };
  },

  sendMessage(aiSessionId, message, options = {}) {
    const { artifact, attachments } = options;
    return {
      type: AI_SESSIONS_SEND_MESSAGE,
      payload: api.post(`/ai-sessions/${aiSessionId}/messages`, {
        role: 'user',
        content: message,
        ...(artifact !== undefined ? { artifact } : {}),
        ...(attachments?.length ? { attachments } : {}),
      }),
    };
  },

  // Generic partial update of an ai-session (PUT /ai-sessions/:id). The caller
  // owns the full patch shape (e.g. a combined top-level `name` + nested
  // `storefront` title-sync body); the server deep-merges nested objects and
  // arrays must be written via $set.
  update(id, patch) {
    return {
      type: AI_SESSIONS_UPDATE_CURRENT,
      payload: api.put(`/ai-sessions/${id}`, patch),
    };
  },

  clearCurrent() {
    return { type: AI_SESSIONS_CLEAR_CURRENT };
  },

  appendMessage(message) {
    return { type: AI_SESSIONS_APPEND_MESSAGE, payload: message };
  },

  setTyping(id, typing, text) {
    return { type: AI_SESSIONS_SET_TYPING, payload: { id, typing, text } };
  },

  startPreview(id) {
    return {
      type: AI_SESSIONS_START_PREVIEW,
      payload: api.post(`/ai-sessions/${id}/preview/start`),
    };
  },

  stopPreview(id) {
    return {
      type: AI_SESSIONS_STOP_PREVIEW,
      payload: api.post(`/ai-sessions/${id}/preview/stop`),
    };
  },
};

export const initialState = {
  all: {},
  current: null,
  typing: {},
  thinkingText: {},
};

function toMap(results) {
  const map = {};
  if (results) {
    for (const session of results) {
      map[session.id] = session;
    }
  }
  return map;
}

function setInMap(all, session) {
  if (!session?.id) return all;
  return { ...all, [session.id]: session };
}

function mergeInMap(all, session) {
  if (!session?.id) return all;
  return { ...all, [session.id]: { ...all[session.id], ...session } };
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case AI_SESSIONS_FETCH_ALL:
      return {
        ...state,
        all: toMap(action.payload?.results),
      };

    case AI_SESSIONS_FETCH:
      return {
        ...state,
        current: action.payload,
        all: setInMap(state.all, action.payload),
      };

    case AI_SESSIONS_CREATE:
      return {
        ...state,
        current: action.payload,
        all: setInMap(state.all, action.payload),
      };

    case AI_SESSIONS_DELETE: {
      const { [action.payload?.id]: _, ...rest } = state.all;
      const { [action.payload?.id]: __, ...typing } = state.typing || {};
      const { [action.payload?.id]: ___, ...thinkingText } =
        state.thinkingText || {};
      return {
        ...state,
        current:
          state.current?.id === action.payload?.id ? null : state.current,
        all: rest,
        typing,
        thinkingText,
      };
    }

    case AI_SESSIONS_UPDATE_CURRENT: {
      const current = state.current
        ? { ...state.current, ...action.payload }
        : action.payload;
      return {
        ...state,
        current,
        all: mergeInMap(state.all, action.payload),
      };
    }

    case AI_SESSIONS_CLEAR_CURRENT:
      return {
        ...state,
        current: null,
      };

    case AI_SESSIONS_APPEND_MESSAGE: {
      if (!state.current) return state;
      const current = {
        ...state.current,
        messages: {
          ...state.current.messages,
          count: (state.current.messages?.count || 0) + 1,
          results: [...(state.current.messages?.results || []), action.payload],
        },
      };
      return {
        ...state,
        current,
        all: setInMap(state.all, current),
      };
    }

    case AI_SESSIONS_SET_TYPING: {
      const { id, typing, text } = action.payload || {};
      if (!id) return state;
      const nextThinkingText = { ...(state.thinkingText || {}) };
      if (typing && typeof text === 'string' && text.trim()) {
        nextThinkingText[id] = text.trim();
      } else if (!typing) {
        delete nextThinkingText[id];
      }
      return {
        ...state,
        typing: { ...(state.typing || {}), [id]: Boolean(typing) },
        thinkingText: nextThinkingText,
      };
    }

    case AI_SESSIONS_START_PREVIEW:
      if (!state.current) return state;
      return {
        ...state,
        current: {
          ...state.current,
          app_detail: {
            ...state.current.app_detail,
            preview: action.payload?.preview,
          },
        },
      };

    case AI_SESSIONS_STOP_PREVIEW:
      if (!state.current) return state;
      return {
        ...state,
        current: {
          ...state.current,
          app_detail: {
            ...state.current.app_detail,
            preview: null,
          },
        },
      };

    default:
      return state;
  }
}

export default actions;
